import { forwardRef, useEffect, useRef, useState, type SelectHTMLAttributes } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { BottomSheet } from './BottomSheet'

export interface SelectOption {
  value: string
  label: string
  // S005: optional badge chip rendered alongside the label (e.g. a room/unit
  // number next to a customer name in a picker). Optional so every existing
  // caller stays backward-compatible.
  chip?: string
  // Optional muted second line under the label (SearchableDropdown only) for
  // data-dense pickers, e.g. the bank-recon expense picker's
  // category/property/description context. Optional so every existing caller
  // stays backward-compatible.
  description?: string
}

// P006: string-union size replaces the native numeric `size` attribute —
// Omit it from the extended HTML attributes (no caller passes the native
// numeric `size` to <Select>, verified by grep).
export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string
  error?: string
  hint?: string
  options: SelectOption[]
  placeholder?: string
  selectClassName?: string
  // P006: default 'md' maps to '' so default output stays byte-identical.
  // 'sm' appends the literal 'select-sm' class to BOTH render paths (desktop
  // <select> and the mobile trigger <button>, which already carries the
  // select base classes — density matches automatically).
  size?: 'sm' | 'md'
  /**
   * Title for the mobile bottom sheet when the Select has no visible `label`
   * (its label sits beside it instead). Falls back to nothing, as before.
   */
  sheetTitle?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className = '', selectClassName = '', label, error, hint, options, placeholder, id, required, size = 'md', sheetTitle, ...props },
    ref,
  ) => {
    const sizeClass = size === 'sm' ? 'select-sm' : ''
    const selectId = id || props.name
    // MUI-2: on mobile, tapping opens an in-app bottom sheet instead of the OS
    // native picker. The native <select> stays mounted (hidden on mobile) so it
    // remains the source of truth for value/required/form semantics; the sheet
    // just calls the same onChange. Pure Tailwind breakpoint switch — no media
    // query hook (none exists in this codebase).
    const [sheetOpen, setSheetOpen] = useState(false)
    const { value, onChange, disabled } = props
    // The mobile trigger label must reflect the CURRENT value. For controlled
    // callers that's `props.value`; for react-hook-form `register()` (uncontrolled,
    // passes no `value`) the hidden native <select> is the source of truth, so we
    // mirror it in local state — updated on choose + native change, and synced from
    // the ref on mount for RHF defaultValues. Without this the mobile trigger showed
    // a bare chevron with no selected text (QA B-047b).
    const [internalValue, setInternalValue] = useState<string>(
      () => String(value ?? props.defaultValue ?? ''),
    )
    const effectiveValue = value !== undefined ? String(value) : internalValue
    const selected = options.find((o) => String(o.value) === effectiveValue)
    const triggerLabel = selected?.label ?? placeholder ?? ''

    // Keep our own handle to the hidden native <select> (the form source of
    // truth — it holds the caller's ref, e.g. react-hook-form's register ref)
    // while still forwarding that ref.
    const innerRef = useRef<HTMLSelectElement | null>(null)
    const setRefs = (el: HTMLSelectElement | null) => {
      innerRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) (ref as React.MutableRefObject<HTMLSelectElement | null>).current = el
    }

    // RHF applies its defaultValues to the element via the register ref, which is
    // not reflected in `props.value` — sync the trigger from the native <select>
    // once on mount so an initial default shows on the mobile trigger.
    useEffect(() => {
      if (value === undefined && innerRef.current) {
        setInternalValue(innerRef.current.value)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const choose = (v: string) => {
      // A bare onChange({target:{value}}) does NOT make react-hook-form pick up
      // the value for an uncontrolled field — RHF reads the registered <select>
      // element. So set the real (hidden) <select> value via the native setter
      // first, THEN notify onChange (for controlled callers + RHF's value store).
      // Together this makes the chosen value reach the form on submit.
      const el = innerRef.current
      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLSelectElement.prototype,
        'value',
      )?.set
      if (el && setter) setter.call(el, v)
      setInternalValue(v)
      onChange?.({ target: { value: v, name: props.name } } as React.ChangeEvent<HTMLSelectElement>)
      setSheetOpen(false)
    }

    return (
      <div className={`form-control ${className || 'w-full'}`}>
        {label && (
          <label className="label whitespace-normal" htmlFor={selectId}>
            <span className="label-text">
              {label}
              {required && <span className="text-error ml-0.5" aria-hidden="true">*</span>}
            </span>
          </label>
        )}

        {/* Desktop: native select (unchanged behavior). Hidden on mobile. */}
        <select
          ref={setRefs}
          id={selectId}
          required={required}
          className={`hidden sm:inline-flex select select-bordered w-full ${error ? 'select-error' : ''} ${sizeClass} ${selectClassName}`}
          {...props}
          onChange={(e) => {
            setInternalValue(e.target.value)
            onChange?.(e)
          }}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Mobile: trigger button → bottom sheet. */}
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={sheetOpen}
          onClick={() => !disabled && setSheetOpen(true)}
          // bg-none suppresses DaisyUI .select's built-in background-image arrow on the
          // mobile trigger — it already renders its own ChevronDown (double-chevron fix,
          // EP-108 mobile review).
          className={`sm:hidden select select-bordered bg-none w-full flex items-center justify-between text-left ${
            error ? 'select-error' : ''
          } ${!selected ? 'text-base-content/40' : ''} ${sizeClass} ${selectClassName}`}
        >
          <span className="truncate leading-none">{triggerLabel}</span>
          <ChevronDown className="w-4 h-4 shrink-0 opacity-60" aria-hidden="true" />
        </button>

        <BottomSheet isOpen={sheetOpen} onClose={() => setSheetOpen(false)} title={label ?? sheetTitle} ariaLabel={label ?? sheetTitle ?? placeholder}>
          <ul role="listbox" aria-label={label ?? placeholder}>
            {options.map((option) => {
              const isSelected = String(option.value) === effectiveValue
              return (
                <li key={option.value} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    // Explicit accessible name: VoiceOver uses this (it overrides the
                    // visible span content for naming), and it surfaces the option in
                    // the iOS WKWebView a11y tree so automation can target it — the
                    // listbox/option role otherwise hides the inner text from the iOS
                    // accessibility tree, which breaks automation-driver lookups.
                    aria-label={option.label}
                    onClick={() => choose(option.value)}
                    className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-left hover:bg-base-200 active:bg-base-300 ${
                      isSelected ? 'font-semibold text-primary' : ''
                    }`}
                  >
                    <span className="truncate leading-none">{option.label}</span>
                    {isSelected && <Check className="w-4 h-4 shrink-0" aria-hidden="true" />}
                  </button>
                </li>
              )
            })}
          </ul>
        </BottomSheet>

        {(error || hint) && (
          <label className="label whitespace-normal">
            <span className={`label-text-alt ${error ? 'text-error' : ''}`}>
              {error || hint}
            </span>
          </label>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'
